import apiClient from './client';
import { getCachedLocalStorage } from '../../utils/storage';
import type {
  DiscoveryRequestSummary, DiscoveryRequestDetail,
  Stakeholder, SpecVersion, SpecVersionSummary,
  UserLookupResult, ExternalPortalContext, SSEEvent,
} from '../../features/discovery/types';

const BASE = '/api/discovery';

export const discoveryApi = {
  createRequest: (description: string) =>
    apiClient.post<DiscoveryRequestSummary>(`${BASE}/requests`, { description }),

  listRequests: (status?: string) =>
    apiClient.get<DiscoveryRequestSummary[]>(`${BASE}/requests`, {
      params: status ? { status } : undefined,
    }),

  getRequest: (id: string) =>
    apiClient.get<DiscoveryRequestDetail>(`${BASE}/requests/${id}`),

  updateStatus: (id: string, status: string) =>
    apiClient.patch(`${BASE}/requests/${id}/status`, { status }),

  addStakeholder: (id: string, data: { email: string; name?: string; context_note: string; language?: string }) =>
    apiClient.post<Stakeholder>(`${BASE}/requests/${id}/stakeholders`, data),

  toggleNotifications: (requestId: string, stakeholderId: string, enabled: boolean) =>
    apiClient.patch(`${BASE}/requests/${requestId}/stakeholders/${stakeholderId}/notifications`, {
      email_notifications: enabled,
    }),

  uploadAttachment: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`${BASE}/requests/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteRequest: (id: string) =>
    apiClient.delete<{ ok: boolean }>(`${BASE}/requests/${id}`),

  deleteAttachment: (requestId: string, attachmentId: string) =>
    apiClient.delete<{ ok: boolean; filename: string }>(`${BASE}/requests/${requestId}/attachments/${attachmentId}`),

  resumeConversation: (requestId: string) =>
    apiClient.post(`${BASE}/requests/${requestId}/resume`),

  resolveAction: (requestId: string, contextNote: string) =>
    apiClient.post(`${BASE}/requests/${requestId}/resolve-action`, { context_note: contextNote }),

  generateSpec: (id: string) =>
    apiClient.post<SpecVersion>(`${BASE}/requests/${id}/generate-spec`),

  listSpecVersions: (id: string) =>
    apiClient.get<SpecVersionSummary[]>(`${BASE}/requests/${id}/spec-versions`),

  getSpecVersion: (id: string, version: number) =>
    apiClient.get<SpecVersion>(`${BASE}/requests/${id}/spec-versions/${version}`),

  /** Authenticated download; use this instead of opening the export URL in a new tab (no auth header). */
  exportSpec: (id: string, format: 'md' | 'pdf') =>
    apiClient.get<Blob>(`${BASE}/requests/${id}/export`, {
      params: { format },
      responseType: 'blob',
    }),

  getUnreadCount: () =>
    apiClient.get<{ count: number }>(`${BASE}/unread-count`),

  userLookup: (query: string) =>
    apiClient.get<UserLookupResult[]>(`${BASE}/user-lookup`, { params: { q: query } }),

  // Stakeholder view (authenticated)
  getStakeholderView: (requestId: string) =>
    apiClient.get<ExternalPortalContext>(`${BASE}/requests/${requestId}/stakeholder-view`),

  // External portal (token-based, no auth header needed but apiClient adds it harmlessly)
  getExternalPortal: (token: string) =>
    apiClient.get<ExternalPortalContext>(`${BASE}/portal/${token}`),
};

/**
 * SSE streaming helper for agent chat.
 * Connects to the chat endpoint and yields parsed SSE events.
 */
export function streamChat(
  url: string,
  content: string,
  onToken: (text: string) => void,
  onActions: (actions: SSEEvent['actions']) => void,
  onDone: (messageId: string) => void,
  onError: (error: Error) => void,
  onMessageDone?: () => void,
): AbortController {
  const controller = new AbortController();
  const token = getCachedLocalStorage('quazi_access_token');

  fetch(`${apiClient.defaults.baseURL}${url}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ content }),
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event: SSEEvent = JSON.parse(line.slice(6));
              if (event.type === 'token' && event.content) onToken(event.content);
              else if (event.type === 'message_done') onMessageDone?.();
              else if (event.type === 'actions') onActions(event.actions || []);
              else if (event.type === 'done') onDone(event.message_id || '');
            } catch {
              // skip malformed events
            }
          }
        }
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') onError(err);
    });

  return controller;
}

/**
 * SSE streaming for external portal (token-based, no auth).
 */
export function streamExternalChat(
  token: string,
  content: string,
  onToken: (text: string) => void,
  onActions: (actions: SSEEvent['actions']) => void,
  onDone: (messageId: string) => void,
  onError: (error: Error) => void,
): AbortController {
  const controller = new AbortController();

  fetch(`${apiClient.defaults.baseURL}${BASE}/portal/${token}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content }),
    signal: controller.signal,
  })
    .then(async (response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event: SSEEvent = JSON.parse(line.slice(6));
              if (event.type === 'token' && event.content) onToken(event.content);
              else if (event.type === 'actions') onActions(event.actions || []);
              else if (event.type === 'done') onDone(event.message_id || '');
            } catch {
              // skip
            }
          }
        }
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') onError(err);
    });

  return controller;
}
